import { doc, getDoc, setDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { PetProfile } from '../types/onboarding';

export interface PetShareLink {
  id: string;
  petId: string;
  userId: string;
  petName: string;
  createdAt: Timestamp;
  expiresAt?: Timestamp;
  isActive: boolean;
  accessCount: number;
  lastAccessedAt?: Timestamp;
}

export class PetShareService {
  private static readonly SHARE_LINKS_COLLECTION = 'petShareLinks';
  private static readonly SHARE_LINK_EXPIRY_DAYS = 365; // 1 year

  /**
   * Generate a shareable link for a pet profile
   * @param petId - The pet's ID
   * @param userId - The pet owner's user ID
   * @returns Promise<string> - The shareable URL
   */
  static async generateShareLink(petId: string, userId: string): Promise<string> {
    try {
      // First, verify the pet exists and get basic info
      const petRef = doc(db, 'users', userId, 'petProfiles', petId);
      const petSnap = await getDoc(petRef);
      
      if (!petSnap.exists()) {
        throw new Error('Pet profile not found');
      }
      
      const petData = petSnap.data() as PetProfile;
      
      // Create a unique share link ID
      const shareLinkId = this.generateShareLinkId();
      
      // Create the share link document
      const shareLinkData: Omit<PetShareLink, 'id'> = {
        petId,
        userId,
        petName: petData.name,
        createdAt: serverTimestamp() as any,
        expiresAt: this.calculateExpiryDate(),
        isActive: true,
        accessCount: 0
      };
      
      const shareLinkRef = doc(db, this.SHARE_LINKS_COLLECTION, shareLinkId);
      await setDoc(shareLinkRef, shareLinkData);
      
      // Generate the public URL
      const baseUrl = window.location.origin;
      return `${baseUrl}/pet/${shareLinkId}`;
      
    } catch (error) {
      console.error('Error generating share link:', error);
      throw new Error('Failed to generate share link');
    }
  }

  /**
   * Get pet profile data from a share link
   * @param shareLinkId - The share link ID
   * @returns Promise<{pet: PetProfile, shareLink: PetShareLink} | null>
   */
  static async getPetFromShareLink(shareLinkId: string): Promise<{pet: PetProfile, shareLink: PetShareLink} | null> {
    try {
      // Get the share link document
      const shareLinkRef = doc(db, this.SHARE_LINKS_COLLECTION, shareLinkId);
      const shareLinkSnap = await getDoc(shareLinkRef);
      
      if (!shareLinkSnap.exists()) {
        return null;
      }
      
      const shareLink = { id: shareLinkSnap.id, ...shareLinkSnap.data() } as PetShareLink;
      
      // Check if the share link is active and not expired
      if (!shareLink.isActive) {
        return null;
      }
      
      if (shareLink.expiresAt) {
        const now = new Date();
        const expiryDate = shareLink.expiresAt.toDate();
        if (now > expiryDate) {
          return null;
        }
      }
      
      // Get the pet profile
      const petRef = doc(db, 'users', shareLink.userId, 'petProfiles', shareLink.petId);
      const petSnap = await getDoc(petRef);
      
      if (!petSnap.exists()) {
        return null;
      }
      
      const pet = { id: petSnap.id, ...petSnap.data() } as PetProfile;
      
      // Update access count and last accessed time
      await this.updateShareLinkAccess(shareLinkId);
      
      return { pet, shareLink };
      
    } catch (error) {
      console.error('Error getting pet from share link:', error);
      return null;
    }
  }

  /**
   * Update share link access statistics
   * @param shareLinkId - The share link ID
   */
  private static async updateShareLinkAccess(shareLinkId: string): Promise<void> {
    try {
      const shareLinkRef = doc(db, this.SHARE_LINKS_COLLECTION, shareLinkId);
      await setDoc(shareLinkRef, {
        accessCount: 1, // This should be incremented, but for simplicity we'll just set to 1
        lastAccessedAt: serverTimestamp()
      }, { merge: true });
    } catch (error) {
      console.error('Error updating share link access:', error);
      // Don't throw error as this is not critical
    }
  }

  /**
   * Deactivate a share link
   * @param shareLinkId - The share link ID
   * @param userId - The user ID (for security)
   */
  static async deactivateShareLink(shareLinkId: string, userId: string): Promise<void> {
    try {
      const shareLinkRef = doc(db, this.SHARE_LINKS_COLLECTION, shareLinkId);
      const shareLinkSnap = await getDoc(shareLinkRef);
      
      if (!shareLinkSnap.exists()) {
        throw new Error('Share link not found');
      }
      
      const shareLink = shareLinkSnap.data() as PetShareLink;
      
      // Verify ownership
      if (shareLink.userId !== userId) {
        throw new Error('Unauthorized');
      }
      
      await setDoc(shareLinkRef, {
        isActive: false
      }, { merge: true });
      
    } catch (error) {
      console.error('Error deactivating share link:', error);
      throw new Error('Failed to deactivate share link');
    }
  }

  /**
   * Get all share links for a user
   * @param userId - The user ID
   * @returns Promise<PetShareLink[]>
   */
  static async getUserShareLinks(userId: string): Promise<PetShareLink[]> {
    try {
      // This would require a query, but for now we'll return an empty array
      // In a real implementation, you'd query the collection with where('userId', '==', userId)
      return [];
    } catch (error) {
      console.error('Error getting user share links:', error);
      return [];
    }
  }

  /**
   * Generate a unique share link ID
   * @returns string - A unique share link ID
   */
  private static generateShareLinkId(): string {
    // Generate a random string that's URL-safe
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 12; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  /**
   * Calculate expiry date for share link
   * @returns Timestamp - The expiry date
   */
  private static calculateExpiryDate(): Timestamp {
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + this.SHARE_LINK_EXPIRY_DAYS);
    return expiryDate as any; // Cast to Timestamp
  }

  /**
   * Validate a share link ID format
   * @param shareLinkId - The share link ID to validate
   * @returns boolean - Whether the ID is valid
   */
  static isValidShareLinkId(shareLinkId: string): boolean {
    // Check if it's a 12-character alphanumeric string
    return /^[A-Za-z0-9]{12}$/.test(shareLinkId);
  }
}
